/**
 * FixDrawItemsDrawer — slide-in "Fix to DrawReady" punch list.
 * Missing documents grouped BY DOCUMENT TYPE across started line items.
 * Each item offers: Request by email (contractor doc request via /form-requests)
 * + Upload. Score updates live as items are cleared.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle2, AlertTriangle, Upload, Mail, Loader2, ShieldCheck, ArrowRight, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { useResumeRefresh } from '../hooks/useResumeRefresh';

const NAVY = '#0b3b6f';

const FORM_TYPE = { coi: 'coi', w9: 'w9', lien: 'lien_waiver', invoice: 'invoice' };
const CONTRACTOR_DOCS = new Set(['coi', 'w9', 'lien']); // email is contractor-directed

function Ring({ score = 0, size = 76, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, score)) / 100);
  const col = score >= 100 ? '#22c55e' : score >= 60 ? '#f97316' : '#ef4444';
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: 'stroke-dashoffset .7s cubic-bezier(.22,1,.36,1)' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-extrabold text-white tabular-nums">{Math.round(score)}</span>
        <span className="text-[8px] text-white/60 tracking-widest">/100</span>
      </div>
    </div>
  );
}

export default function FixDrawItemsDrawer({ open, onClose, projectId, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [requested, setRequested] = useState({});
  const [coiOpen, setCoiOpen] = useState(null); // item key awaiting expiration date
  const [coiDate, setCoiDate] = useState('');
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await apiService.getDrawReadinessDetail(projectId);
      setDetail(res.data);
    } catch (e) {
      // Aborted/network-error requests (tab was backgrounded) have no response —
      // don't scare the user with an error; useResumeRefresh retries on return.
      if (e?.response) toast.error('Could not load your fix list');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) { setLoading(true); setRequested({}); setCoiOpen(null); load(); }
  }, [open, load]);

  // Auto-recover when the user returns to the tab / reconnects (iOS Safari
  // suspends the page and aborts in-flight requests on app rotation).
  useResumeRefresh(() => { if (open && projectId) load(); });

  const keyOf = (item, doc) => `${doc}:${item.phase_id}:${item.contractor_id || 'x'}`;

  const reloadAll = async () => { await load(); if (onChanged) onChanged(); };

  const doUpload = async (item, doc, file, expiration) => {
    if (!file) return;
    const k = keyOf(item, doc);
    setBusy(k);
    try {
      if (doc === 'coi') {
        const fd = new FormData(); fd.append('file', file); fd.append('expiration_date', expiration);
        await apiService.uploadContractorCOI(item.contractor_id, fd);
      } else if (doc === 'w9') {
        const fd = new FormData(); fd.append('file', file);
        await apiService.uploadContractorW9(item.contractor_id, fd);
      } else if (doc === 'invoice') {
        await apiService.uploadPhaseReceiptFile(item.phase_id, file);
      } else if (doc === 'lien') {
        const fd = new FormData(); fd.append('file', file);
        await apiService.uploadPhaseLienWaiver(item.phase_id, fd);
      }
      toast.success('Document uploaded — readiness updated');
      setCoiOpen(null); setCoiDate('');
      await reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const triggerUpload = (item, doc) => {
    if (doc === 'coi') {
      // COI needs an expiration date first
      const k = keyOf(item, doc);
      setCoiOpen(coiOpen === k ? null : k); setCoiDate('');
      return;
    }
    const ref = fileRefs.current[keyOf(item, doc)];
    if (ref) ref.click();
  };

  const doEmail = async (item, doc) => {
    if (!item.contractor_id || !item.contractor_email) {
      toast.error('Add this contractor\u2019s email first, then request the document.');
      return;
    }
    const k = keyOf(item, doc) + '-email';
    setBusy(k);
    try {
      await apiService.createFormRequest({
        project_id: projectId,
        contractor_id: item.contractor_id,
        form_types: [FORM_TYPE[doc]],
        phase_id: (doc === 'lien' || doc === 'invoice') ? item.phase_id : undefined,
        notes: `Requested for line item: ${item.phase_name}`,
      }, window.location.origin);
      toast.success(`Request emailed to ${item.contractor_name || 'the contractor'}`);
      setRequested((r) => ({ ...r, [keyOf(item, doc)]: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the request.');
    } finally {
      setBusy(null);
    }
  };

  const groups = detail?.groups || [];
  const complete = !loading && groups.length === 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex justify-end" data-testid="fix-drawer">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} data-testid="fix-drawer-overlay" />
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-[slidein_.3s_ease]" style={{ animationName: 'slidein' }}>
        <style>{`@keyframes slidein{from{transform:translateX(24px);opacity:.6}to{transform:none;opacity:1}}`}</style>

        {/* Header */}
        <div className="text-white p-5" style={{ background: NAVY }}>
          <div className="flex items-start justify-between">
            <p className="font-bold text-lg">Fix to DrawReady</p>
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="fix-drawer-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center gap-4 mt-3">
            <Ring score={detail?.score ?? 0} />
            <div>
              <p className="text-sm font-bold">{complete ? "You're bank-ready \ud83c\udf89" : 'Collect these to hit 100'}</p>
              <p className="text-xs text-blue-100 mt-0.5 truncate max-w-[220px]">{detail?.project_name || ''}</p>
              <p className="text-[11px] text-white/60 mt-1">Updates live as docs come in</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : complete ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="fix-drawer-complete">
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                <ShieldCheck className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="font-bold text-slate-900">Every required document is in.</p>
              <p className="text-sm text-slate-500 mt-1">{detail?.project_name} is bank-ready — submit your draw.</p>
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.doc_type} className="mb-4" data-testid={`fix-group-${g.doc_type}`}>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold px-1 mb-1">{g.label} · {g.needed} needed</p>
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                  {g.items.map((item) => {
                    const k = keyOf(item, g.doc_type);
                    const isBusy = busy === k;
                    const emailBusy = busy === k + '-email';
                    const wasRequested = requested[k];
                    const canEmail = CONTRACTOR_DOCS.has(g.doc_type) || g.doc_type === 'invoice';
                    const hasContractor = !!(item.contractor_id && item.contractor_email);
                    return (
                      <div key={k} className="p-3" data-testid={`fix-item-${k}`}>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">{item.phase_name}</p>
                            <p className="text-[11px] text-slate-400 truncate flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {item.contractor_name || item.contractor_company || 'No contractor assigned'}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2 pl-6">
                          {canEmail && (
                            <button
                              onClick={() => doEmail(item, g.doc_type)}
                              disabled={emailBusy || wasRequested || !hasContractor}
                              className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg text-white disabled:opacity-50 ${wasRequested ? 'bg-emerald-600' : ''}`}
                              style={!wasRequested ? { background: NAVY } : {}}
                              title={!hasContractor ? 'Add a contractor email to request by email' : ''}
                              data-testid={`fix-email-${k}`}
                            >
                              {emailBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : wasRequested ? <CheckCircle2 className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                              {wasRequested ? 'Requested' : 'Request by email'}
                            </button>
                          )}
                          <button
                            onClick={() => triggerUpload(item, g.doc_type)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            data-testid={`fix-upload-${k}`}
                          >
                            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            Upload
                          </button>
                          {g.doc_type !== 'coi' && (
                            <input
                              type="file"
                              ref={(el) => { fileRefs.current[k] = el; }}
                              className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; doUpload(item, g.doc_type, f); }}
                            />
                          )}
                        </div>

                        {/* COI expiration mini-form */}
                        {g.doc_type === 'coi' && coiOpen === k && (
                          <div className="mt-2 pl-6 flex items-end gap-2" data-testid={`fix-coi-form-${k}`}>
                            <div>
                              <label className="text-[10px] text-slate-500">COI expiration</label>
                              <input type="date" value={coiDate} onChange={(e) => setCoiDate(e.target.value)}
                                className="block h-8 text-xs border border-slate-300 rounded-lg px-2" data-testid={`fix-coi-date-${k}`} />
                            </div>
                            <button
                              onClick={() => { if (!coiDate) { toast.error('Pick the COI expiration date'); return; } fileRefs.current[k]?.click(); }}
                              className="h-8 inline-flex items-center gap-1 text-xs font-bold px-3 rounded-lg text-white" style={{ background: NAVY }}
                              data-testid={`fix-coi-choose-${k}`}
                            >
                              <Upload className="w-3 h-3" /> Choose file
                            </button>
                            <input
                              type="file"
                              ref={(el) => { fileRefs.current[k] = el; }}
                              className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; doUpload(item, 'coi', f, coiDate); }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100">
          <button
            onClick={() => (complete ? navigate(`/project/${projectId}?tab=loan`) : onClose())}
            className={`w-full py-3 rounded-xl font-bold text-white inline-flex items-center justify-center gap-2 ${complete ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-300'}`}
            data-testid="fix-drawer-cta"
          >
            {complete ? <>Request a draw <ArrowRight className="w-4 h-4" /></> : 'Clear all items to request a draw'}
          </button>
        </div>
      </div>
    </div>
  );
}
